/**
 * Standardized Button Component
 * Provides consistent button styling across the application
 */

import { forwardRef, ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'default' | 'link';
  size?: 'sm' | 'md' | 'lg' | 'default' | 'icon';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'default',
      size = 'default',
      loading = false,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      disabled,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    // Map 'default' to 'primary' for backward compatibility
    const effectiveVariant = variant === 'default' ? 'primary' : variant;
    const effectiveSize = size === 'default' ? 'md' : size;

    // Base styles
    const baseStyles =
      'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95';

    // Variant styles — primary/outline/ghost/link use CSS vars so branding colors apply
    const variantStyles = {
      primary:
        'bg-[var(--primary,#2563EB)] text-[var(--primary-foreground,#fff)] hover:brightness-90 focus:ring-[var(--primary,#2563EB)] shadow-md hover:shadow-lg',
      secondary:
        'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 focus:ring-gray-500 border border-gray-300 dark:border-gray-600',
      outline:
        'border-2 border-[var(--primary,#2563EB)] text-[var(--primary,#2563EB)] hover:bg-[var(--primary,#2563EB)]/10 focus:ring-[var(--primary,#2563EB)] bg-transparent',
      ghost:
        'text-[var(--primary,#2563EB)] hover:bg-[var(--primary,#2563EB)]/10 focus:ring-[var(--primary,#2563EB)] bg-transparent',
      danger:
        'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 shadow-md hover:shadow-lg',
      success:
        'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 shadow-md hover:shadow-lg',
      link:
        'text-[var(--primary,#2563EB)] hover:underline focus:ring-[var(--primary,#2563EB)] bg-transparent p-0',
    };

    // Size styles
    const sizeStyles = {
      sm: 'text-xs px-3 py-1.5 gap-1.5',
      md: 'text-sm px-4 py-2.5 gap-2',
      lg: 'text-base px-6 py-3 gap-2.5',
      icon: 'p-2',
    };

    // Icon size based on button size
    const iconSize = {
      sm: 'w-3.5 h-3.5',
      md: 'w-4 h-4',
      lg: 'w-5 h-5',
      icon: 'w-5 h-5',
    };

    const combinedClassName = `
      ${baseStyles}
      ${variantStyles[effectiveVariant as keyof typeof variantStyles] || variantStyles.primary}
      ${sizeStyles[effectiveSize as keyof typeof sizeStyles] || sizeStyles.md}
      ${fullWidth ? 'w-full' : ''}
      ${className}
    `.trim();

    return (
      <button
        ref={ref}
        className={combinedClassName}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <Loader2 className={`${iconSize[effectiveSize as keyof typeof iconSize]} animate-spin flex-shrink-0`} />
        )}
        {!loading && icon && iconPosition === 'left' && (
          <span className={`${iconSize[effectiveSize as keyof typeof iconSize]} flex items-center justify-center flex-shrink-0`}>
            {icon}
          </span>
        )}
        {children}
        {!loading && icon && iconPosition === 'right' && (
          <span className={`${iconSize[effectiveSize as keyof typeof iconSize]} flex items-center justify-center flex-shrink-0`}>
            {icon}
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
